import { makeAutoObservable } from 'mobx';
import sortBy from 'lodash/sortBy';

import { CustomGradeCode } from '@/customGrading/constants/customGradeCodes';
import { ProductEntity } from '@/product/types';

import MediaSourcesAPI from '../api/MediaSourcesAPI';
import { setupPresetRepository } from '../repository/SetupPresetRepository';

import Media, { MediaModel } from './Media';
import MediaSource from './MediaSource';
import { MediaSubFile } from './MediaSubFile';
import { AVAILABLE_POLISHES, POLISHES_SORTING } from './MediaSubFormat';
import SetupPreset, {
  I_FACEUP_FLUOR_DARK,
  I_OFFICE,
  I_SIDE_FLUOR_DARK,
  OFFICE,
  SETUP_PRESET_GRADING_CODE_MAP,
} from './SetupPreset';
import SetupPresetGroup from './SetupPresetGroup';

const BLOCKED_SETUP_PRESETS = [I_FACEUP_FLUOR_DARK, I_SIDE_FLUOR_DARK];

// todo maybe mediaItems and polishImages should be in specific components to reduce dependencies in component tree
export default class MediaCollection {
  product: ProductEntity;

  items: Media[];

  mediaSourcesAPI: MediaSourcesAPI;

  constructor(modelData: MediaModel[], product: ProductEntity) {
    makeAutoObservable(this, {
      product: false,
      mediaSourcesAPI: false,
    });

    this.product = product;

    const setupPresetOrder = setupPresetRepository.getSetupPresetOrder();
    const filteredData = modelData.filter((d) => !BLOCKED_SETUP_PRESETS.includes(d.sp)); // prevent displaying FLUOR_DARK preset on frontend

    const mediaCollection = filteredData.map((mediaModelData) => new Media(mediaModelData, this.product));

    this.items = sortBy(mediaCollection, (media) => {
      const index = setupPresetOrder.findIndex((spId) => spId === media.setupPreset.id);

      if (index < 0) {
        return Infinity;
      }

      return index;
    });

    this.mediaSourcesAPI = new MediaSourcesAPI();
  }

  get mediaItems(): Media[] {
    return this.items ? this.items.filter((item) => (item.isImage || item.isVideo) && !item.isEmpty) : [];
  }

  get mediaItemsWithoutCustom(): Media[] {
    return this.mediaItems ? this.mediaItems.filter((m) => m.setupPreset.isStandard === true) : [];
  }

  get mediaItemsWithEmptySources(): Media[] {
    return this.items ? this.items.filter((item) => item.isImage || item.isVideo) : [];
  }

  get polishImages(): MediaSubFile[] {
    const polishMedias = this.items ? this.items.filter((item) => item.isPolish) : [];

    if (
      polishMedias.length === 0 ||
      polishMedias[0].mediaSources.length === 0 ||
      polishMedias[0].mediaSources[0].mediaSubFiles.length === 0
    ) {
      return [];
    }

    return this.getPolishImagesFromMedias(polishMedias);
  }

  get dmcModels(): Media[] {
    return this.items ? this.items.filter((item) => item.is3DModel) : [];
  }

  get i3dReports(): Media[] {
    return this.items ? this.items.filter((item) => item.isI3DReport) : [];
  }

  get i3dComparativeReports(): Media[] {
    return this.items ? this.items.filter((item) => item.isI3DComparativeReport) : [];
  }

  get htmlReports(): Media[] {
    return this.items ? this.items.filter((item) => item.isPolish) : [];
  }

  get allMediaSetupPresets(): SetupPreset[] {
    if (!this.mediaItems) {
      return [];
    }

    return [...new Set(this.mediaItems.map((m) => m.setupPreset))];
  }

  removeMediaSource(mediaSourceToRemove: MediaSource) {
    if (this.items) {
      this.items.forEach((media) => {
        media.mediaSources = media.mediaSources.filter((mediaSource) => mediaSource.id !== mediaSourceToRemove.id);
      });
    }

    // TODO: MOVE TO STORE
    return this.mediaSourcesAPI.remove(mediaSourceToRemove.id);
  }

  getMediaBySetupPreset(setupPreset: Nullable<SetupPreset>): Nullable<Media> {
    if (!setupPreset || !this.items || !this.items.length) {
      return null;
    }

    return this.items.find((m) => m.setupPreset.id === setupPreset.id) ?? null;
  }

  getMediaBySetupPresetGroup(spg: Nullable<SetupPresetGroup>): Nullable<Media> {
    return spg ? this.getMediaByMultipleSetupPresets(spg.sp) : null;
  }

  /**
   * Looking for first suitable SP depending on spIds list. It's used for SP grouping
   */
  getSuitableSPFromSPList(sps: Nullable<Nullable<SetupPreset>[]>): Nullable<SetupPreset> {
    const media = this.getMediaByMultipleSetupPresets(sps);

    if (!media) {
      return null;
    }

    return media.setupPreset;
  }

  getMediaByMultipleSetupPresets(sps: Nullable<Nullable<SetupPreset>[]>): Nullable<Media> {
    if (!sps || sps.length === 0) {
      return null;
    }

    for (let i = 0; i < sps.length; i++) {
      const media = this.getMediaBySetupPreset(sps[i]);

      if (media && !media.isEmpty) {
        return media;
      }
    }

    return null;
  }

  getPolishImagesFromMedias(polishMedias: Media[]): MediaSubFile[] {
    if (!polishMedias || !polishMedias.length) {
      return [];
    }

    const subFiles = polishMedias[0].mediaSources[0].mediaSubFiles;

    const filteredSubFiles = subFiles
      ? subFiles.filter((item) => item.mediaSubFormat && AVAILABLE_POLISHES.includes(item.mediaSubFormat.id))
      : [];
    const sortedPolishes = POLISHES_SORTING.map((polishId) => (
      filteredSubFiles.find((polish) => polish.mediaSubFormat.id === polishId)
    ));

    return sortedPolishes.filter(Boolean) as MediaSubFile[]; // returns only not-falsy values
  }

  get hasSpreadMedia(): boolean {
    const hasOfficeImage = Boolean(this.getMediaBySetupPreset(setupPresetRepository.findSetupPresetById(I_OFFICE)));
    const hasOfficeVideo = Boolean(this.getMediaBySetupPreset(setupPresetRepository.findSetupPresetById(OFFICE)));

    return hasOfficeImage || hasOfficeVideo;
  }

  findMediaSourceById(id: string): Nullable<MediaSource> {
    return this.findMediaAndMediaSourceByMsId(id)?.mediaSource ?? null;
  }

  findMediaAndMediaSourceByMsId(msId: string): Nullable<{ media: Media; mediaSource: MediaSource }> {
    if (!this.mediaItems || this.mediaItems.length === 0) {
      return null;
    }

    for (let i = 0; i < this.mediaItems.length; i++) {
      if (!this.mediaItems[i].mediaSources || this.mediaItems[i].mediaSources.length === 0) {
        continue;
      }

      const mediaSource = this.mediaItems[i].mediaSources.find((ms) => ms.id === msId);

      if (mediaSource) {
        return { media: this.mediaItems[i], mediaSource };
      }
    }

    return null;
  }

  hasMediaBySetupPresets(spsIds: number[]): boolean {
    if (!this.allMediaSetupPresets || this.allMediaSetupPresets.length === 0) {
      return false;
    }

    const setupPresets = this.allMediaSetupPresets.filter(({ id }) => spsIds.includes(id));

    return setupPresets.length > 0;
  }

  getCustomGradingCode(): CustomGradeCode | undefined {
    let customGradeCode: CustomGradeCode | undefined;

    // keep in mind that here we find the first SP in order of SETUP_PRESET_GRADING_CODE_MAP
    // there might be a situation when a media collection has more than one available grading code,
    //
    // maybe in future we will have switcher between grading codes, but for now we need to take fist in order
    const spIds = [...SETUP_PRESET_GRADING_CODE_MAP.keys()];

    for (let i = 0; i < spIds.length; i++) {
      const setupPresetType = spIds[i];

      if (!customGradeCode && this.hasMediaBySetupPresets([setupPresetType])) {
        customGradeCode = SETUP_PRESET_GRADING_CODE_MAP.get(setupPresetType);

        break;
      }
    }

    return customGradeCode;
  }
}
